import { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import * as amplitude from "@amplitude/analytics-browser";
import { Button, OTPStep } from '@unified-checkout/design-system';
import { useEnterKeyPress } from '@app/dashboard/hooks/useEnterKeyPress';
import { Spinner } from '@app/dashboard/assets/icons';
import type { IRegisterOtpStep } from '@app/dashboard/types/outerLayout/register';
import {
  leadsRegisterVerifyOtp, leadsSubmitPhone as requestOtp,
  registerResendOtp, registerVerifyOtp
} from '@app/dashboard/services/outer_layout/register';
import styles from "../register.module.scss";


const SUBMIT_BTN_ID = 'register-otp-submit-btn';

const OtpStep = ({
  loading,
  setLoading,
  redirectToLogin,
  setCurrentStep,
  STEPS,
  phoneNumber,
  replacedServerErrMsg,
  leadToken
}: IRegisterOtpStep
) => {
  const { t } = useTranslation();
  useEnterKeyPress(SUBMIT_BTN_ID);

  const [otp, setOtp] = useState('');
  const [isValid, setIsValid] = useState(false);
  const [serverErrMsg, setServerErrMsg] = useState('');
  const [serverSuccessMsg, setServerSuccessMsg] = useState('');
  const [otpResetTimerKey, setOtpResetTimerKey] = useState(0);

  useEffect(() => {
    setServerErrMsg("");
    setServerSuccessMsg("");
  }, [otp]);

  const onValidHandler = (valid: boolean) => {
    setIsValid(valid);
  };

  const onResendOTP = () => {
    setServerErrMsg("");
    setServerSuccessMsg("");
    setLoading(true);

    registerResendOtp("register")
      .then(() => {
        setOtpResetTimerKey(prevKey => prevKey + 1);
        setIsValid(false);
        setServerSuccessMsg(t('form.otpResent'));
      })
      .catch((error) => {
        const data = error.response.data;
        const message = data?.message;

        setServerErrMsg(message || t('errors.somethingWentWrong'));
      })
      .finally(() => {
        setLoading(false);
      });

  };

  const onLeadsResendOTP = () => {
    setServerErrMsg("");
    setServerSuccessMsg("");
    setLoading(true);

    requestOtp(phoneNumber, leadToken)
      .then(() => {
        setOtpResetTimerKey(prevKey => prevKey + 1);
        setIsValid(false);
        setServerSuccessMsg(t('form.otpResent'));
      })
      .catch((error) => {
        const status = error.response?.status;
        const data = error.response?.data;
        const message = data?.message;
        const phoneAlreadyRegisterMsg = data?.phone_number?.message;
        const youDontHavePermessionMsg = "You do not have permission to perform this action.";

        if (phoneAlreadyRegisterMsg) {
          setServerErrMsg(t('outerLayout.form.register.errors.phoneExists'));
        } else if (status === 403 && message === youDontHavePermessionMsg) {
          setServerErrMsg(t('outerLayout.form.errors.tryInAnHour'));
        } else if (message) {
          setServerErrMsg(message);
        } else {
          setServerErrMsg(t('errors.somethingWentWrong'));
        }
      })
      .finally(() => {
        setLoading(false);
      });

  };

  const handleOtpSubmit = () => {
    setServerErrMsg("");
    setServerSuccessMsg("");
    setLoading(true);

    registerVerifyOtp(otp)
      .then(() => {
        amplitude.track('sign_up_otp');

        setCurrentStep(STEPS.REGISTER);
      })
      .catch((error) => {
        const data = error.response.data;
        const message = data?.message;

        setServerErrMsg(message ? t('outerLayout.form.errors.wrongOtp') : t('errors.somethingWentWrong'));
      })
      .finally(() => {
        setLoading(false);
      });
  };

  const handleLeadsOtpSubmit = () => {
    setServerErrMsg("");
    setServerSuccessMsg("");
    setLoading(true);

    leadsRegisterVerifyOtp(otp, leadToken)
      .then(() => {
        setCurrentStep(STEPS.REGISTER);
      })
      .catch((error) => {

        const data = error.response?.data;
        const message = data?.message;
        const invalidOTPMsg = data?.otp?.message;

        setServerErrMsg(
          invalidOTPMsg ?
            t('outerLayout.form.errors.wrongOtp')
            :
            (
              message ?
                message
                :
                t('errors.somethingWentWrong')
            )
        );
      })
      .finally(() => {
        setLoading(false);
      });
  };

  return (
    <>
      <OTPStep
        otp={otp}
        setOtp={setOtp}
        otpResetTimerKey={otpResetTimerKey}
        onValid={onValidHandler}
        onResendOTP={leadToken ? onLeadsResendOTP : onResendOTP}
        resendText={t('form.resendCodeIn')}
        resendBtnText={t('form.resend')}
        resendTimer={150}
        length={6}
        loading={loading}
      />
      <div className='form__submit'>
        {serverErrMsg && !serverSuccessMsg &&
          <div className='form__submit__server-err'>{
            serverErrMsg.includes('{{loginLink}}') ?
              replacedServerErrMsg(serverErrMsg)
              :
              serverErrMsg
          }</div>
        }
        {serverSuccessMsg && !serverErrMsg && <div className='form__submit__server-success'>{serverSuccessMsg}</div>}

        <Button
          id={SUBMIT_BTN_ID}
          size='md'
          color='primary'
          variant='contained'
          text={loading ? undefined : t('outerLayout.form.next')}
          leadingIcon={loading ? Spinner : undefined}
          disabled={!isValid || loading}
          onClickHandler={leadToken ? handleLeadsOtpSubmit : handleOtpSubmit}
        />
      </div>
      <div className={styles["register-form__already-have-account"]}>
        <span className={styles["register-form__already-have-account__text"]}>{t('outerLayout.form.register.alreadyHaveAcc')}</span>
        <div className={styles["register-form__already-have-account__action"]}>
          <Button
            size='sm'
            color='primary'
            variant='link'
            text={t('outerLayout.form.register.signIn')}
            onClickHandler={redirectToLogin}
          />
        </div>
      </div>
    </>
  )
}

export default OtpStep;